import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input } from '../../components/ui/input-simple';
import { Button } from '../../components/ui/button-simple';
import { Search, MapPin, Factory, Globe, Plus, AlertCircle, Loader2, Trash2, Eye, Edit3, Filter, X, Building2, Users, TrendingUp, Package, ChevronLeft, ChevronRight, Sprout, BookOpen, MessageSquare, ClipboardList } from 'lucide-react';
import { municipalities } from '../../data/municipalities';
import { AddProcessorForm } from '../../components/forms/AddProcessorForm';
import { ProcessorImportExportButtons } from '../../components/buttons/ProcessorImportExportButtons';
import { processorsAPI } from '../../services/api';
import { useProcessors } from '../../hooks/useApi';
import { ConfirmDeleteModal, StatusUpdateModal } from '../../components/modals/ConfirmDeleteModal';

// Add animations
const styles = `
  @keyframes fadeInUp {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }
  .animate-fade-in-up { animation: fadeInUp 0.5s ease-out forwards; }
  .animate-fade-in-up:nth-child(1) { animation-delay: 0.1s; }
  .animate-fade-in-up:nth-child(2) { animation-delay: 0.2s; }
  .animate-fade-in-up:nth-child(3) { animation-delay: 0.3s; }
  .animate-fade-in-up:nth-child(4) { animation-delay: 0.4s; }
  .animate-fade-in-up:nth-child(5) { animation-delay: 0.5s; }
  .animate-fade-in-up:nth-child(6) { animation-delay: 0.6s; }

  @keyframes slideDown {
    from { opacity: 0; transform: translateY(-20px); }
    to { opacity: 1; transform: translateY(0); }
  }
  .animate-slide-down { animation: slideDown 0.6s ease-out forwards; }

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  .animate-fade-in { animation: fadeIn 0.3s ease-out forwards; }

  @keyframes slideUp {
    from { opacity: 0; transform: translateY(30px) scale(0.95); }
    to { opacity: 1; transform: translateY(0) scale(1); }
  }
  .animate-slide-up { animation: slideUp 0.4s ease-out forwards; }
`;

if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = styles;
  if (!document.head.querySelector('style[data-admin-processors-animations]')) {
    styleSheet.setAttribute('data-admin-processors-animations', 'true');
    document.head.appendChild(styleSheet);
  }
}

export function AdminProcessorsDirectory() {
  const navigate = useNavigate();
  const {
    processors,
    loading,
    error,
    fetchProcessors,
    addProcessor,
    updateProcessor,
    removeProcessor
  } = useProcessors();

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMunicipality, setSelectedMunicipality] = useState('all');
  const [selectedBusinessType, setSelectedBusinessType] = useState('all');
  const [selectedMarketReach, setSelectedMarketReach] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedProcessor, setSelectedProcessor] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editProcessor, setEditProcessor] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, processor: null });
  const [statusModal, setStatusModal] = useState({ isOpen: false, processor: null });
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  useEffect(() => {
    const loadData = async () => {
      try { await fetchProcessors(() => processorsAPI.getAll()); } catch (error) {}
    };
    loadData();
  }, [fetchProcessors]);

  const extractMunicipalityFromAddress = (address) => {
    if (!address || typeof address !== 'string') return null;
    const parts = address.split(',').map(part => part.trim());
    if (parts.length >= 2) {
      const municipality = parts[parts.length - 2];
      if (municipality && municipality.length > 2) return municipality;
    }
    return null;
  };

  const computedMunicipalities = useMemo(() => {
    if (!processors || processors.length === 0) return municipalities;
    const extractedMunicipalities = processors.map(p => {
      let muni = p.municipality;
      if (!muni || muni.trim() === '') muni = extractMunicipalityFromAddress(p.address);
      return muni;
    }).filter(Boolean);
    const uniqueMunicipalities = [...new Set(extractedMunicipalities)].sort();
    return uniqueMunicipalities.length > 0 ? uniqueMunicipalities : municipalities;
  }, [processors]);

  const filteredProcessors = useMemo(() => {
    return processors.filter(processor => {
      const matchesSearch = (processor.businessName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (processor.ownerName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (processor.barangay || '').toLowerCase().includes(searchTerm.toLowerCase());
      let processorMunicipality = processor.municipality;
      if (!processorMunicipality || processorMunicipality.trim() === '') {
        processorMunicipality = extractMunicipalityFromAddress(processor.address);
      }
      const matchesMunicipality = selectedMunicipality === 'all' || processorMunicipality === selectedMunicipality;
      const matchesBusinessType = selectedBusinessType === 'all' || processor.businessType === selectedBusinessType;
      const matchesMarketReach = selectedMarketReach === 'all' || processor.marketReach === selectedMarketReach;
      const matchesStatus = selectedStatus === 'all' || processor.businessStatus === selectedStatus;
      return matchesSearch && matchesMunicipality && matchesBusinessType && matchesMarketReach && matchesStatus;
    });
  }, [processors, searchTerm, selectedMunicipality, selectedBusinessType, selectedMarketReach, selectedStatus]);

  const paginatedProcessors = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredProcessors.slice(startIndex, endIndex);
  }, [filteredProcessors, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredProcessors.length / itemsPerPage);

  useEffect(() => { setCurrentPage(1); }, [searchTerm, selectedMunicipality, selectedBusinessType, selectedMarketReach, selectedStatus]);

  const handleAddProcessor = (newProcessor) => {
    addProcessor(newProcessor);
  };

  const handleEditProcessor = async () => {
    try {
      await fetchProcessors(() => processorsAPI.getAll());
      setEditProcessor(null);
    } catch (error) {}
  };

  const handleBulkImport = async () => {
    await fetchProcessors(() => processorsAPI.getAll());
  };

  const handleDeleteClick = (processor) => {
    setDeleteModal({ isOpen: true, processor });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModal.processor) return;
    setIsDeleting(true);
    try {
      try { await processorsAPI.delete(deleteModal.processor.id); } catch (apiError) {}
      removeProcessor(deleteModal.processor.id);
      if (selectedProcessor && selectedProcessor.id === deleteModal.processor.id) {
        setSelectedProcessor(null);
      }
      setDeleteModal({ isOpen: false, processor: null });
    } catch (error) {
      alert('Failed to delete processor. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ isOpen: false, processor: null });
  };

  const handleStatusClick = (processor) => {
    setStatusModal({ isOpen: true, processor });
  };

  const handleStatusConfirm = async (newStatus) => {
    if (!statusModal.processor) return;
    setIsUpdatingStatus(true);
    try {
      try { await processorsAPI.updateStatus(statusModal.processor.id, newStatus); } catch (apiError) {}
      const updatedProcessor = { ...statusModal.processor, statusOfBusiness: newStatus };
      updateProcessor(statusModal.processor.id, updatedProcessor);
      if (selectedProcessor && selectedProcessor.id === statusModal.processor.id) {
        setSelectedProcessor(updatedProcessor);
      }
      setStatusModal({ isOpen: false, processor: null });
    } catch (error) {
      alert('Failed to update processor status. Please try again.');
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleStatusCancel = () => {
    setStatusModal({ isOpen: false, processor: null });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active': return 'bg-green-100 text-green-800';
      case 'Inactive': return 'bg-red-100 text-red-800';
      case 'Seasonal': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getMarketReachColor = (reach) => {
    switch (reach) {
      case 'International': return 'bg-purple-100 text-purple-800';
      case 'National': return 'bg-blue-100 text-blue-800';
      case 'Regional': return 'bg-green-100 text-green-800';
      case 'Local': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8 bg-gradient-to-r from-amber-600 to-amber-700 rounded-2xl shadow-xl p-5 text-white animate-slide-down relative z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-white/20 backdrop-blur-sm p-3 rounded-xl">
                <Factory className="h-7 w-7" />
              </div>
              <div>
                <h1 className="text-2xl font-bold mb-1">Cacao Processors Directory (Admin)</h1>
                <p className="text-amber-50 max-w-2xl text-sm">
                  Comprehensive directory of cacao processing businesses across Bohol.
                </p>
              </div>
            </div>
            <div className="flex gap-3 items-center">
              <Button
                onClick={() => navigate('/admin/profile-requests')}
                className="bg-amber-800/80 hover:bg-amber-900 text-white font-semibold flex items-center gap-2 px-4 py-2 rounded-xl shadow border border-amber-400/40 text-xs"
              >
                <ClipboardList className="h-4 w-4 text-amber-200" />
                Pending Requests
              </Button>
              <div className="relative z-50 bg-white/95 rounded-lg p-1">
                <ProcessorImportExportButtons 
                  processors={processors}
                  onImport={handleBulkImport}
                  userRole="admin"
                />
              </div>
              <Button 
                onClick={() => setShowAddForm(true)}
                className="bg-green-600 text-white hover:bg-green-700 font-semibold flex items-center gap-2 px-6 shadow-lg whitespace-nowrap"
              >
                <Plus className="h-5 w-5" />
                Add Processor
              </Button>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white p-6 rounded-2xl shadow-lg mb-8 border border-gray-200 relative z-10">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="h-5 w-5 text-amber-600" />
            <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <Input
                placeholder="Search business or owner..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-11 rounded-xl border-gray-300 focus:border-amber-500 focus:ring-amber-500"
              />
            </div>
            
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5 pointer-events-none" />
              <select 
                value={selectedMunicipality} 
                onChange={(e) => setSelectedMunicipality(e.target.value)}
                className="flex h-11 w-full rounded-xl border border-gray-300 bg-background px-3 pl-10 py-2 text-sm focus:border-amber-500 focus:ring-amber-500"
              >
                <option value="all">All Municipalities</option>
                {computedMunicipalities.map(m => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>
            
            <div className="relative">
              <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5 pointer-events-none" />
              <select 
                value={selectedBusinessType} 
                onChange={(e) => setSelectedBusinessType(e.target.value)}
                className="flex h-11 w-full rounded-xl border border-gray-300 bg-background px-3 pl-10 py-2 text-sm focus:border-amber-500 focus:ring-amber-500"
              >
                <option value="all">All Types</option>
                <option value="Cooperative">Cooperative</option>
                <option value="Private">Private</option>
                <option value="Partnership">Partnership</option>
              </select>
            </div>
            
            <div className="relative">
              <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5 pointer-events-none" />
              <select 
                value={selectedMarketReach} 
                onChange={(e) => setSelectedMarketReach(e.target.value)}
                className="flex h-11 w-full rounded-xl border border-gray-300 bg-background px-3 pl-10 py-2 text-sm focus:border-amber-500 focus:ring-amber-500"
              >
                <option value="all">All Markets</option>
                <option value="Local">Local</option>
                <option value="Regional">Regional</option>
                <option value="National">National</option>
                <option value="International">International</option>
              </select>
            </div>
            
            <div className="relative">
              <TrendingUp className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5 pointer-events-none" />
              <select 
                value={selectedStatus} 
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="flex h-11 w-full rounded-xl border border-gray-300 bg-background px-3 pl-10 py-2 text-sm focus:border-amber-500 focus:ring-amber-500"
              >
                <option value="all">All Status</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
                <option value="Seasonal">Seasonal</option>
              </select>
            </div>
          </div>
          
          <div className="mt-6 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="h-2 w-2 bg-amber-500 rounded-full"></div>
              <p className="text-sm text-gray-600 font-medium">
                Showing {((currentPage - 1) * itemsPerPage) + 1}-{Math.min(currentPage * itemsPerPage, filteredProcessors.length)} of {filteredProcessors.length} processors
              </p>
            </div>
            <Button
              variant="outline"
              onClick={() => {
                setSearchTerm('');
                setSelectedMunicipality('all');
                setSelectedBusinessType('all');
                setSelectedMarketReach('all');
                setSelectedStatus('all');
              }}
              className="flex items-center gap-2 rounded-xl hover:bg-gray-50"
            >
              <X className="h-4 w-4" />
              Clear Filters
            </Button>
          </div>
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-amber-600 mr-2" />
            <span className="text-gray-600">Loading processors...</span>
          </div>
        )}

        {/* Error */}
        {error && !loading && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
              <span className="text-red-800 font-medium">Error loading processors</span>
            </div>
            <p className="text-red-600 mt-2">{error}</p>
            <Button onClick={() => window.location.reload()} className="mt-4 bg-red-600 hover:bg-red-700">
              Retry
            </Button>
          </div>
        )}

        {/* Processors Grid */}
        {!loading && !error && filteredProcessors.length > 0 && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {paginatedProcessors.map((processor) => (
                <div key={processor.id} className="group bg-white rounded-2xl shadow-md hover:shadow-2xl transition-all duration-300 border border-gray-200 hover:border-amber-300 overflow-hidden flex flex-col animate-fade-in-up" style={{ height: '450px' }}>
                  <div className="bg-gradient-to-r from-amber-50 to-amber-100 p-3 border-b border-amber-200 flex-shrink-0">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="text-lg text-amber-900 font-bold mb-1 group-hover:text-amber-700 transition-colors line-clamp-1">
                          {processor.businessName}
                        </h3>
                        <div className="flex items-center mt-1 text-gray-700">
                          <MapPin className="h-4 w-4 mr-1 flex-shrink-0 text-amber-600" />
                          <span className="text-sm">{processor.address}</span>
                        </div>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold shadow-sm ${getStatusColor(processor.businessStatus)}`}>
                        {processor.businessStatus}
                      </span>
                    </div>
                  </div>
                  
                  <div className="p-3 flex-1 flex flex-col overflow-y-auto">
                    <div className="space-y-2 flex-1">
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4 text-amber-600" />
                          <span className="text-sm text-gray-600 font-medium">Owner</span>
                        </div>
                        <span className="font-semibold text-gray-900 text-sm">{processor.ownerName}</span>
                      </div>
                      
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4 text-blue-600" />
                          <span className="text-sm text-gray-600 font-medium">Type</span>
                        </div>
                        <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">{processor.businessType}</span>
                      </div>
                      
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                        <div className="flex items-center gap-2">
                          <TrendingUp className="h-4 w-4 text-green-600" />
                          <span className="text-sm text-gray-600 font-medium">Years Operating</span>
                        </div>
                        <span className="font-semibold text-gray-900">{processor.yearsInOperation} years</span>
                      </div>
                      
                      <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <Globe className="h-4 w-4 text-purple-600" />
                          <span className="text-sm text-gray-600 font-medium">Market Reach</span>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getMarketReachColor(processor.marketReach)}`}>
                          {processor.marketReach}
                        </span>
                      </div>
                      
                      <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <Package className="h-4 w-4 text-orange-600" />
                          <span className="text-sm text-gray-600 font-medium">Capacity</span>
                        </div>
                        <span className="font-semibold text-gray-900">{processor.processingCapacity} kg/mo</span>
                      </div>
                    </div>
                    
                    <div className="space-y-1.5 mt-2 flex-shrink-0">
                      <div className="flex gap-2">
                        <Button 
                          className="flex-1 bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white font-semibold flex items-center justify-center gap-1.5 rounded-xl shadow-md hover:shadow-lg transition-all py-2 h-9 text-xs"
                          onClick={() => setSelectedProcessor(processor)}
                        >
                          <Eye className="h-4 w-4" />
                          View Profile
                        </Button>
                        <Button 
                          className="bg-amber-600 hover:bg-amber-700 text-white font-semibold flex items-center justify-center gap-1.5 rounded-xl shadow-md hover:shadow-lg transition-all px-3 h-9 text-xs"
                          onClick={() => navigate(`/messages?partnerId=processor_${processor.id}&partnerName=${encodeURIComponent(processor.businessName || processor.nameOfBusiness || 'Processor')}&partnerRole=processor`)}
                          title="Message Processor"
                        >
                          <MessageSquare className="h-4 w-4" />
                          Message
                        </Button>
                      </div>
                      
                      <div className="flex gap-1.5">
                        <Button 
                          variant="outline"
                          className="flex-1 text-green-600 border-2 border-green-300 hover:bg-green-50 hover:border-green-500 rounded-xl transition-all flex items-center justify-center gap-1 py-2 h-9 text-xs"
                          onClick={() => setEditProcessor(processor)}
                        >
                          <Edit3 className="h-3.5 w-3.5" />
                          Edit
                        </Button>
                        <Button 
                          variant="outline"
                          className="flex-1 text-blue-600 border-2 border-blue-300 hover:bg-blue-50 hover:border-blue-500 rounded-xl transition-all flex items-center justify-center gap-1 py-2 h-9 text-xs"
                          onClick={() => handleStatusClick(processor)}
                        >
                          <Edit3 className="h-3.5 w-3.5" />
                          Status
                        </Button>
                        <Button 
                          variant="outline"
                          className="flex-1 text-red-600 border-2 border-red-300 hover:bg-red-50 hover:border-red-500 rounded-xl transition-all flex items-center justify-center gap-1 py-2 h-9 text-xs"
                          onClick={() => handleDeleteClick(processor)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          Delete
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-4 mt-8 bg-white p-6 rounded-2xl shadow-lg">
                <Button
                  variant="outline"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="flex items-center gap-2 px-6 rounded-xl hover:bg-amber-50 hover:border-amber-300 disabled:opacity-50"
                >
                  <ChevronLeft className="h-5 w-5" />
                  Previous
                </Button>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Page</span>
                  <span className="px-4 py-2 bg-gradient-to-r from-amber-600 to-amber-700 text-white font-bold rounded-xl shadow-md">
                    {currentPage}
                  </span>
                  <span className="text-sm text-gray-600">of {totalPages}</span>
                </div>
                <Button
                  variant="outline"
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="flex items-center gap-2 px-6 rounded-xl hover:bg-amber-50 hover:border-amber-300 disabled:opacity-50"
                >
                  Next
                  <ChevronRight className="h-5 w-5" />
                </Button>
              </div>
            )}
          </>
        )}

        {/* Detail Modal */}
        {selectedProcessor && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 animate-fade-in">
            <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto animate-slide-up">
              <div className="p-6">
                <div className="bg-gradient-to-r from-amber-600 to-amber-700 p-6 rounded-t-2xl">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="bg-white/20 backdrop-blur-sm p-3 rounded-xl">
                        <Building2 className="h-8 w-8 text-white" />
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold text-white">Processor Profile</h2>
                        <p className="text-amber-50 text-sm">Comprehensive business information</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="ghost" 
                        onClick={() => { setSelectedProcessor(null); setEditProcessor(selectedProcessor); }}
                        className="text-white hover:bg-white/20 rounded-xl h-12 w-12 p-0 transition-all hover:scale-110"
                      >
                        <Edit3 className="h-6 w-6" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        onClick={() => setSelectedProcessor(null)}
                        className="text-white hover:bg-white/20 rounded-xl h-12 w-12 p-0 transition-all hover:scale-110"
                      >
                        <X className="h-7 w-7" />
                      </Button>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-6 p-6 max-h-[70vh] overflow-y-auto">
                  <div className="bg-gradient-to-br from-amber-50 to-white p-6 rounded-2xl border border-amber-200 shadow-sm">
                    <div className="flex items-center gap-2 mb-4">
                      <Building2 className="h-5 w-5 text-amber-600" />
                      <h3 className="text-lg font-bold text-amber-900">Business Information</h3>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Name of Business</span>
                        <p className="font-semibold text-gray-900">{selectedProcessor.nameOfBusiness || selectedProcessor.businessName || 'Not specified'}</p>
                      </div>
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Address</span>
                        <p className="font-semibold text-gray-900">{selectedProcessor.address || 'Not specified'}</p>
                      </div>
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Owner</span>
                        <p className="font-semibold text-gray-900">{selectedProcessor.nameOfTheOwner || selectedProcessor.ownerName || 'Not specified'}</p>
                      </div>
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Business Type</span>
                        <p className="font-semibold text-gray-900">{selectedProcessor.typeOfBusiness || selectedProcessor.businessType || 'Not specified'}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Add Form */}
        {showAddForm && (
          <AddProcessorForm
            onClose={() => setShowAddForm(false)}
            onSave={handleAddProcessor}
          />
        )}

        {/* Edit Form */}
        {editProcessor && (
          <AddProcessorForm
            processorData={editProcessor}
            isEditing={true}
            onClose={() => setEditProcessor(null)}
            onSave={handleEditProcessor}
          />
        )}

        {/* Delete Confirmation Modal */}
        <ConfirmDeleteModal
          isOpen={deleteModal.isOpen}
          onClose={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
          title="Delete Processor Profile"
          message={`Are you sure you want to delete ${deleteModal.processor?.businessName || deleteModal.processor?.nameOfBusiness}? This action cannot be undone.`}
          isLoading={isDeleting}
        />

        {/* Status Update Modal */}
        <StatusUpdateModal
          isOpen={statusModal.isOpen}
          onClose={handleStatusCancel}
          onConfirm={handleStatusConfirm}
          currentStatus={statusModal.processor?.statusOfBusiness || statusModal.processor?.businessStatus}
          isLoading={isUpdatingStatus}
        />
      </div>
    </div>
  );
}
